import {
  Badge,
  Box,
  Button,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';
import { tokens } from '../../global/theme/tokens';
import { ECTopbar } from './ECTopbar';
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';
import FolderIcon from '@mui/icons-material/Folder';
import SettingsIcon from '@mui/icons-material/Settings';
import { ExoSkeleton } from '../exo/ExoSkeleton';
import { useState } from 'react';
import ExoDialog from '../exo/ExoDialog';
import { ECSettings } from './ECSettings';
import { NewEmailIntegration } from '../new/NewEmailIntegration';

export const folderOrder = [
  'INBOX',
  'Drafts',
  'Sent Items',
  'Spam',
  'Trash',
  'Archive',
];

export const ECInboxList = ({
  onOpen,
  inboxes,
  onSelect,
  selectedFolder,
  folderMap,
  width,
  isLoading,
  onOpenSettings,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  const { t } = useTranslation();

  const [openSettings, setOpenSettings] = useState(false);

  return (
    <Box
      sx={{
        width,
      }}
      className="flex flex-col gap-2 relative justify-between h-full"
    >
      <Box className="flex flex-col gap-2 h-full overflow-y-auto w-full">
        <ECTopbar
          label={t('Inboxes')}
          ActionIcon={AddIcon}
          actionTooltip={t('Add E-Mail Account')}
          onAction={() => onOpen(true)}
        />
        {isLoading ? (
          <ExoSkeleton n={8} />
        ) : inboxes ? (
          inboxes.map((inbox) => (
            <Inbox
              key={inbox.id}
              inbox={inbox}
              onSelect={onSelect}
              selectedFolder={selectedFolder}
              folderMap={folderMap}
            />
          ))
        ) : (
          <>Add Account</>
        )}
      </Box>
      <Box>
        <IconButton size="small" onClick={onOpenSettings}>
          <SettingsIcon />
        </IconButton>
      </Box>
    </Box>
  );
};

const Inbox = ({ inbox, onSelect, selectedFolder, folderMap }) => {
  const { label, color, imapUsername } = inbox;
  const { folders } = inbox.inbox;

  const sortedFolders = folders.sort((a, b) => {
    return folderOrder.indexOf(a.name) - folderOrder.indexOf(b.name);
  });

  return (
    <Box className="px-2 pb-4">
      <Typography
        fontSize={11}
        fontWeight={600}
        className=" opacity-50 flex flex-col items-start gap-0"
      >
        {label}{' '}
        <span
          style={{ fontWeight: 400 }}
          className="pl-2 italic"
        >{`<${imapUsername}>`}</span>
      </Typography>
      {sortedFolders.map((folder) => (
        <Folder
          key={folder.id}
          folder={folder}
          onSelect={onSelect}
          selectedFolder={selectedFolder}
          folderMap={folderMap}
        />
      ))}
    </Box>
  );
};

const Folder = ({ folder, onSelect, selectedFolder, folderMap }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  const { t } = useTranslation();

  const { name, id, unseenCount } = folder;

  const Icon = folderMap[name]?.Icon || FolderIcon;
  const label = folderMap[name]?.label || name;
  const color = folderMap[name]?.color || 'unset';

  const isSelected = selectedFolder?.id === id;

  const formatedUnseenCount = unseenCount > 99 ? '99+' : unseenCount;

  return (
    <Button
      onClick={() => onSelect(folder)}
      sx={{
        padding: '3px 10px',
        justifyContent: 'start',
        borderRadius: '5px',
        bgcolor: isSelected ? colors.card : 'transparent',
        border: `1px solid transparent`,
        ':hover': {
          bgcolor: colors.info + '30',
          border: `1px solid ${colors.info}80`,
        },
      }}
      className="w-full flex gap-2"
    >
      <Box className="flex w-full gap-2 items-center">
        <Icon sx={{ fontSize: '16px', color }} className="opacity-50" />
        <Typography>{label}</Typography>
      </Box>
      {unseenCount > 0 && (
        <Typography
          fontSize={10}
          fontWeight={500}
          sx={{
            bgcolor: colors.info + '30',
            border: `1px solid ${colors.info}80`,
          }}
          className="px-1 rounded-full"
        >
          {formatedUnseenCount}
        </Typography>
      )}
    </Button>
  );
};
